/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, Popover } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon } from '@coursera/cds-icons';

import ChipButton from 'bundles/search-common/components/experiments/filters2024/ChipButton';

import _t from 'i18n!nls/search-common';

export type FilterChipDropdownProps = {
  triggerPrefix?: React.ReactNode;
  triggerCount?: number;
  triggerLabel?: string;
  panelHeader?: React.ReactNode;
  children: React.ReactNode;
  onApply?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onReset?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onClose?: () => void;
};

export const FilterChipDropdown = ({
  triggerPrefix,
  triggerLabel = '',
  triggerCount = 0,
  panelHeader,
  onApply,
  onReset,
  onClose,
  children,
}: FilterChipDropdownProps): React.ReactElement => {
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(popoverAnchor);

  return (
    <>
      <ChipButton
        onClick={(e) => setPopoverAnchor(e.currentTarget)}
        prefix={triggerPrefix}
        suffix={isOpen ? <ChevronUpIcon size="small" /> : <ChevronDownIcon size="small" />}
        isActive={triggerCount > 0}
        count={triggerCount}
        showCount={triggerCount > 1}
      >
        {triggerLabel}
      </ChipButton>
      <Popover
        open={isOpen}
        anchorElement={popoverAnchor}
        onClose={() => {
          onClose?.();
          setPopoverAnchor(null);
        }}
      >
        <Popover.Header>{panelHeader}</Popover.Header>
        <Popover.Body component="div">{children}</Popover.Body>
        <Popover.Footer>
          <Grid container>
            <Grid item>
              <Button
                variant="primary"
                type="button"
                onClick={(e) => {
                  onApply?.(e);
                  setPopoverAnchor(null);
                }}
              >
                {_t('Show results')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="ghost"
                onClick={(e) => {
                  onReset?.(e);
                  setPopoverAnchor(null);
                }}
              >
                {_t('Reset')}
              </Button>
            </Grid>
          </Grid>
        </Popover.Footer>
      </Popover>
    </>
  );
};

export default FilterChipDropdown;
